import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import SeatingPlanTable from "../../Components/SeatingPlanComponents/SeatingPlanTable";
import swal from "sweetalert";
import seating_plan_service from "../../services/seating_plan_service";
import { setHallsList } from "../../reducers/hall_reducer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import halls_service from "../../services/halls_service";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import SeatingPlanTableNew from "../../Components/SeatingPlanComponents/SeatingPlanTableNew";
import { data_example } from "./data_example";


const valueColorForHall: { [key: number]: string } = {
  1: "#4D67EB",
  2: "#FF5347",
  3: "#FFC107",
  4: "#8E24AA",
  5: "#00ACC1",
  6: "#43A047",
};

interface SeatingCell {
  price: number;
  status: "open" | "invitation" | "closed";
  ordered: "yes" | "no";
  category: string;
}

interface SelectedCells {
  [key: number]: {
    [key: number]: SeatingCell;
  };
}

const CreateSeatingPlansPage: React.FC = () => {
  const { id, seating_id } = useParams<{ id: string; seating_id?: string }>();
  const navigation = useNavigate();
  const { state } = useLocation() as {
    state: {
      appliedCells: SelectedCells;
      seatingPlanId?: string;
      seatingPlanName?: string;
    };
  };
  const [selectedValue, setSelectedValue] = useState<number>(1);
  const [circleColor, setCircleColor] = useState<string>("#4D67EB");
  const [price, setPrice] = useState<number>(0);
  const [selectedHall, setSelectedHall] = useState<any>(null);
  const [seatingPlanName, setSeatingPlanName] = useState<string>("");
  const [selectedCells, setSelectedCells] = useState<SelectedCells>({});
  const [appliedCells, setAppliedCells] = useState<SelectedCells>(
    state?.appliedCells || data_example
  );
  const [seatingPlanId, setSeatingPlanId] = useState<string | undefined>(
    seating_id || state?.seatingPlanId
  );
  const [loading, setLoading] = useState(true); // Loader state
  const dispatch = useDispatch();
  const { hallsReducer } = useSelector((state: RootState) => state);
  const { hallsList } = hallsReducer;
  const { i18n } = useTranslation();
  const language = i18n.language;
  useEffect(() => {}, []);
  useEffect(() => {
    setSeatingPlanName(state?.seatingPlanName || "");
    halls_service
      .getHallsList()
      .then((res: any) => {
        const hallsList = res.data.status.halls;
        if (hallsList.length) {
          dispatch(setHallsList(hallsList));
          const hall = hallsList.find((hall: any) => hall.hall_id === id);
          setSelectedHall(hall || {});
        }
        setLoading(false); // Deactivate loader after data is fetched
      })
      .catch(() => {
        setLoading(false); // Deactivate loader in case of error
      });
  }, [dispatch, id]);

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    const value = event.target.value as number;
    setSelectedValue(value);
    setCircleColor(valueColorForHall[value] || "");
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(Number(event.target.value));
  };

  const handleSeatingPlanNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSeatingPlanName(event.target.value);
  };

  const applyChanges = () => {
    setAppliedCells((prev) => {
      const newAppliedCells: SelectedCells = { ...prev };
      Object.keys(selectedCells).forEach((rowIndex) => {
        if (!newAppliedCells[+rowIndex]) {
          newAppliedCells[+rowIndex] = {};
        }
        Object.keys(selectedCells[+rowIndex]).forEach((colIndex) => {
          newAppliedCells[+rowIndex][+colIndex] =
            selectedCells[+rowIndex][+colIndex];
        });
      });
      return newAppliedCells;
    });
    setSelectedCells({});
    // toast.success("Price applied successfully!"); /// error 
  };

  const cancelSelection = () => {
    setSelectedCells({});
    toast.info("Selection cancelled.");
  };

  const handleSave = () => {
    const payload = seatingPlanId
      ? {
          seating_id: seatingPlanId,
          hall_id: id,
          seating_name: seatingPlanName,
          seating_json: JSON.stringify(appliedCells),
        }
      : {
          hall_id: id,
          seating_name: seatingPlanName,
          seating_json: JSON.stringify(appliedCells),
        };

    const savePromise = seatingPlanId
      ? seating_plan_service.updateSeatingPlan(payload)
      : seating_plan_service.createSeatingPlan(payload);
    savePromise
      .then((res) => {
        navigation("/seating-plans");  
        swal("Success", "Seating plan saved successfully!", "success");
      })
      .catch((err) => {
        swal("Error", "Failed to save seating plan.", "error");
      });
  };

  return (
    <Box sx={{
      width: "100%",
      height: "100%",
      paddingBottom: 2,
      overflow: "hidden",
    }}>
      <ToastContainer />
      <Backdrop sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        color: "#fff",
      }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{
        width: "98%",
        backgroundColor: "#fff",
        padding: "10px",
        height: "95%",
        overflowY: "auto",
      }}>
        <Box sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 2,
        }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#E53E3E",
              fontSize: "16px",
              color: "#fff",
              padding: "12px 30px",
              minWidth: "140px",
              fontWeight: "bold",
              boxShadow: "0 4px 8px rgba(229, 62, 62, 0.3)",
              "&:hover": {
                backgroundColor: "#C53030",
                boxShadow: "0 6px 12px rgba(229, 62, 62, 0.4)",
              },
            }}
            size="large"
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
        <Box sx={{
          marginBottom: 2,
          width: "40%",
          marginTop: 2,
        }}>
          <Typography sx={{
            marginBottom: 1,
            fontSize: "18px",
          }}>
            Add New Seating Plan
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={selectedHall?.hallName_en || ""}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
        <Box sx={{
          marginBottom: 2,
          width: "90%",
          marginTop: 2,
        }}>
          <Typography sx={{
            marginBottom: 1,
            fontSize: "18px",
          }}>
            Seating plan Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Default seating plan"
            variant="outlined"
            value={seatingPlanName}
            onChange={handleSeatingPlanNameChange}
            style={{ borderRadius: "4px", padding: "0px" }}
          />
        </Box>
        <Box sx={{
          borderTop: "1px solid #E3E3E3",
          paddingTop: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}>
          <FormControl variant="outlined" style={{ width: "30%" }}>
            <Select value={selectedValue} onChange={handleSelectChange}>
              <MenuItem value={1}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#4D67EB"
                    }}
                  ></span>
                  <Box>Seat Category 1</Box>
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#FF5347"
                    }}
                  ></span>
                  <Box>Seat Category 2</Box>
                </Box>
              </MenuItem>
              <MenuItem value={3}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#FFC107"
                    }}
                  ></span>
                  <Box>Seat Category 3</Box>
                </Box>
              </MenuItem>
              <MenuItem value={4}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#8E24AA"
                    }}
                  ></span>
                  <Box>Seat Category 4</Box>
                </Box>
              </MenuItem>
              <MenuItem value={5}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#00ACC1"
                    }}
                  ></span>
                  <Box>Seat Category 5</Box>
                </Box>
              </MenuItem>
              <MenuItem value={6}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 1,
                      backgroundColor: "#43A047"
                    }}
                  ></span>
                  <Box>Seat Category 6</Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="number"
            variant="outlined"
            placeholder="Price"
            value={price}
            onChange={handlePriceChange}
            style={{ width: "20%" }}
          />
        </Box>
        <Box sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 2,
        }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3CAF2C",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#2E8B22",
              },
              marginRight: 2,
            }}
            onClick={applyChanges}
          >
            Apply Price
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#757575",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#5A5A5A",
              },
            }}
            onClick={cancelSelection}
          >
            Cancel Selection
          </Button>
        </Box>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 40,
        }}>
          <SeatingPlanTableNew
            seatColor={circleColor}
            price={price}
            selectedCells={selectedCells}
            setSelectedCells={setSelectedCells}
            appliedCells={appliedCells}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CreateSeatingPlansPage;
